import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import ApiService from 'global/utils/ApiService';
import { assertApiSuccess } from './apiResponse';

type ApiQueryOptions<TData> = Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'> & {
  queryFn?: () => Promise<TData>;
  /** Reject API responses with isSuccess: false. Defaults to false for backwards compatibility. */
  rejectOnApiError?: boolean;
};

export function useApiQuery<TData = any>(queryKey: readonly unknown[], url: string, payload?: Record<string, any>, options?: ApiQueryOptions<TData>) {
  const { queryFn: customQueryFn, rejectOnApiError = false, ...queryOptions } = options || {};
  return useQuery<TData>({
    queryKey: queryKey as any,
    queryFn:
      customQueryFn ||
      (async () => {
        const response = await ApiService.post(url, payload || {});
        return (rejectOnApiError ? assertApiSuccess(response) : response) as TData;
      }),
    ...queryOptions,
  });
}

/**
 * A variant that uses GET instead of POST.
 */
export function useApiGetQuery<TData = any>(queryKey: readonly unknown[], url: string, options?: ApiQueryOptions<TData>) {
  const { queryFn: _customQueryFn, rejectOnApiError = false, ...queryOptions } = options || {};
  return useQuery<TData>({
    queryKey: queryKey as any,
    queryFn: async () => {
      const response = await ApiService.get(url);
      return (rejectOnApiError ? assertApiSuccess(response) : response) as TData;
    },
    ...queryOptions,
  });
}

export default useApiQuery;
